// Rhythms as step grids: how the assistant writes rhythms, and reads the ones it's shown. One line
// per voice, one character per step, bars split by "|" (spaces ignored):
//   hihat  x.x.x.x.x.x.x.x.|x.x.x.x.x.x.x.x.
//   snare  ....X.......X...|....X..g....X...
//   kick   x.......x.x.....|x.......x.x.....
// x a hit, X accented, g a ghost note, . or - nothing; on a snare part, r and l (R and L accented)
// are hits with their sticking. A drum-set score sounds its voices together as one line of chords
// (as the map's grooves do), so the hits on one step share an accent and a loudness.
import { writeVoice, hitsOf, measureTicks, TICKS } from "../grow/grid";
import { validateScore, SocialError } from "../social/validation";
import { instrumentName, isPitched } from "../instruments";

// How rhythms are written as grids, for Claude (the assistant, and modules' steps).
export const GRID_GUIDE = `# Rhythms you read and write
Rhythms are shown to you, and written by you, as step grids: one line per voice, one character per step, bars split by "|".
  hihat      x.x.x.x.x.x.x.x.|x.x.x.x.x.x.x.x.
  snare      ....X.......X...|....X..g....X...
  kick       x.......x.x.....|x.......x.x.....
- x is a hit, X an accented hit, g a ghost note, . nothing. On a snare-drum part, r and l (R and L accented) are hits with their sticking.
- Drum-set voices: kick, snare, hihat, ride, hihat_foot, tom1, tom2, tom3, floor_tom.
- Steps per bar: 16 for 16th notes in 4/4, 12 for 8th-note triplets (shuffles, swing, 12/8 feels), 8 for 8ths, 24 for 16th-note triplets. In 3/4 use 12 for 16ths; in 6/8 use 12 for 16ths or 6 for 8ths; in 7/8, 14 for 16ths.
- When you write a set to practice, go from simpler to harder, change one thing at a time, keep each rhythm one or two bars (they loop), and name each for what it works on ("Rock 3: 16th-note kick pickups"). Typical tempos: rock 80 to 130, funk 85 to 110, hip hop 80 to 95, shuffle 100 to 140, jazz swing 120 to 200, bossa nova 120 to 140, samba 90 to 105 (felt in 2), metal 120 to 200.
- Write real, idiomatic grooves: the backbeat on 2 and 4 in rock and funk; ghost notes between backbeats in funk; the hi-hat or ride keeping time; fills that resolve to beat 1.`;
export const DRUM_VOICES = { kick: "F4", snare: "C5", hihat: "E5", ride: "F5", hihat_foot: "D4", tom1: "D5", tom2: "B4", tom3: "A4", floor_tom: "G4" };
const VOICE_OF = Object.fromEntries(Object.entries(DRUM_VOICES).map(([name, pitch]) => [pitch, name]));
const SNARE = "C5", VELOCITY = { ghost: 0.25, hit: 0.6, accent: 0.9 };
// Steps a grid may use, in ticks (24 to a quarter note): quarters, 8ths, 8th-note triplets, 16ths,
// 16th-note sextuplets and 32nds.
const STEP_TICKS = [24, 12, 8, 6, 4, 3];
export const MAX_BARS = 8;
const bad = (message) => { throw new SocialError(400, message); };

export function timeSigOf(text = "4/4") {
  const match = /^\s*(\d{1,2})\s*\/\s*(\d{1,2})\s*$/.exec(String(text));
  const timeSig = match && { num: Number(match[1]), type: Number(match[2]) };
  if (!timeSig || timeSig.num < 1 || timeSig.num > 16 || ![2, 4, 8, 16].includes(timeSig.type)) bad(`"${text}" isn't a time signature this can write (like 4/4, 3/4, 6/8 or 7/8).`);
  return timeSig;
}

// What a step's character means: null for nothing, else { level, sticking }.
function stroke(char, snare) {
  if (char === "." || char === "-") return null;
  if (char === "x") return { level: "hit", sticking: "" };
  if (char === "X") return { level: "accent", sticking: "" };
  if (char === "g") return { level: "ghost", sticking: "" };
  if (snare && "rlRL".includes(char)) return { level: char === char.toUpperCase() ? "accent" : "hit", sticking: char.toLowerCase() };
  return bad(`"${char}" isn't a step this can write: use x, X, g or . ${snare ? "(or r, l, R, L for stickings)" : ""}`.trim());
}

// A rhythm written as grids: { instrument: "drumset" | "snare", time_signature, steps_per_bar,
// voices: { kick: "x...|x...", ... } } as a TrueChops score.
export function writtenScore({ instrument = "drumset", time_signature = "4/4", steps_per_bar = 16, voices } = {}) {
  if (!["drumset", "snare"].includes(instrument)) bad("Write rhythms for the drum set or a snare drum.");
  const timeSig = timeSigOf(time_signature), total = measureTicks(timeSig), steps = Math.round(Number(steps_per_bar));
  const step = steps > 0 && total % steps === 0 ? total / steps : 0;
  if (!STEP_TICKS.includes(step)) bad(`${steps_per_bar} steps don't divide a bar of ${timeSig.num}/${timeSig.type} into notes this can write; use ${STEP_TICKS.filter((ticks) => total % ticks === 0).map((ticks) => total / ticks).join(", ")}.`);
  const names = Object.keys(voices || {}).filter((name) => typeof voices[name] === "string" && voices[name].replace(/[\s|.-]/g, ""));
  if (!names.length) bad("Give at least one voice some hits.");
  const allowed = instrument === "snare" ? ["snare"] : Object.keys(DRUM_VOICES);
  for (const name of names) if (!allowed.includes(name)) bad(`"${name}" isn't a voice here; use ${allowed.join(", ")}.`);
  const lines = names.map((name) => ({ name, bars: voices[name].replace(/\s+/g, "").split("|") }));
  const bars = Math.max(...lines.map((line) => line.bars.length));
  if (bars > MAX_BARS) bad(`Write up to ${MAX_BARS} bars.`);
  const measures = [];
  for (let bar = 0; bar < bars; bar++) {
    const strokes = new Map();
    for (const line of lines) {
      const text = line.bars[bar] || "";
      if (text.length > steps) bad(`${line.name}, bar ${bar + 1}, has ${text.length} steps; a bar has ${steps}.`);
      [...text].forEach((char, index) => {
        const hit = stroke(char, instrument === "snare");
        if (!hit) return;
        const at = index * step, both = strokes.get(at) || { pitches: [], levels: [], sticking: "" };
        both.pitches.push(instrument === "snare" ? SNARE : DRUM_VOICES[line.name]);
        both.levels.push(hit.level);
        both.sticking ||= hit.sticking;
        strokes.set(at, both);
      });
    }
    // A step's loudness is its loudest hit's: a ghost note under a hi-hat sounds with the hi-hat.
    const hits = [...strokes].map(([at, { pitches, levels, sticking }]) => {
      const level = levels.includes("accent") ? "accent" : levels.includes("hit") ? "hit" : "ghost";
      return { at, notes: pitches, ornaments: `${sticking}${level === "accent" ? "a" : ""}`, velocity: VELOCITY[level] };
    });
    measures.push({ timeSig, parts: [{ instrument, voices: [writeVoice(hits, timeSig)] }] });
  }
  return validateScore({ parts: { [instrument]: { enabled: true } }, measures }).score;
}

// Grids read top to bottom, as drum notation does: cymbals, toms, snare, kick.
const READ_ORDER = ["ride", "hihat", "tom1", "tom2", "tom3", "floor_tom", "snare", "kick", "hihat_foot"];
// Which of a step's voices an accent belongs to (it's written on the whole step): the snare, else a
// tom, else a cymbal, else the kick.
const ACCENTED = ["snare", "tom1", "tom2", "tom3", "floor_tom", "hihat", "ride", "kick", "hihat_foot"];
// A step's character for one voice of a sounding note, as written above.
function charOf(hit, instrument, voice, voices) {
  const accent = ((hit.ornaments || "").includes("a") || hit.velocity >= 0.8) && (instrument !== "drumset" || voice === ACCENTED.find((name) => voices.includes(name)));
  const ghost = !accent && hit.velocity < 0.35;
  const sticking = instrument === "snare" ? /[rl]/.exec(hit.ornaments || "")?.[0] : null;
  if (sticking) return accent ? sticking.toUpperCase() : sticking;
  return accent ? "X" : ghost ? "g" : "x";
}
// The coarsest step every hit in these bars lands on (16ths if some fall between, say 16ths and
// triplets in one bar).
function stepFor(hitsByBar, total) {
  const all = hitsByBar.flat();
  return STEP_TICKS.find((ticks) => total % ticks === 0 && all.every((hit) => hit.at % ticks === 0)) || 6;
}

// A score's first bars as grids, for the assistant to read: "drum set, 4/4, 16 steps a bar" and a
// line per voice. Piano and other parts get a line saying what they are.
export function gridOf(score, { bars = 2 } = {}) {
  const measures = (score?.measures || []).slice(0, bars), timeSig = measures[0]?.timeSig;
  if (!timeSig) return "";
  const total = measureTicks(timeSig), parts = [...new Set(measures.flatMap((measure) => measure.parts.map((part) => part.instrument)))];
  const out = [];
  for (const instrument of parts) {
    if (isPitched(instrument)) { out.push(`${instrumentName(instrument).toLowerCase()} part (${score.measures.length} bars of ${timeSig.num}/${timeSig.type})`); continue; }
    const hitsByBar = measures.map((measure) => {
      const part = measure.parts.find((item) => item.instrument === instrument);
      return (part?.voices || []).flatMap((voice) => hitsOf(voice) || []);
    });
    const step = stepFor(hitsByBar, total), steps = total / step;
    const voiceOf = (pitch) => instrument === "drumset" ? VOICE_OF[pitch] || pitch : instrument === "snare" ? "snare" : `${instrument} ${pitch}`;
    const lines = new Map();
    hitsByBar.forEach((hits, bar) => {
      for (const hit of hits) {
        const voices = hit.notes.map(voiceOf);
        for (const name of voices) {
          if (!lines.has(name)) lines.set(name, measures.map(() => Array(steps).fill(".")));
          const index = Math.round(hit.at / step);
          if (index < steps) lines.get(name)[bar][index] = charOf(hit, instrument, name, voices);
        }
      }
    });
    if (!lines.size) continue;
    const order = READ_ORDER;
    out.push(`${instrument === "drumset" ? "drum set" : instrument}, ${timeSig.num}/${timeSig.type}, ${steps} steps a bar${step === 8 || step === 4 ? " (triplets)" : ""}:`);
    for (const [name, rows] of [...lines].sort(([a], [b]) => (order.indexOf(a) + 1 || 99) - (order.indexOf(b) + 1 || 99))) out.push(`  ${name.padEnd(10)} ${rows.map((row) => row.join("")).join("|")}`);
  }
  return out.join("\n");
}

// A rhythm in a line: its name, instrument, length and tempo.
export function describe({ title, score, tempo }) {
  const parts = [...new Set(score.measures.flatMap((measure) => measure.parts.map((part) => part.instrument)))];
  const timeSig = score.measures[0].timeSig, names = { drumset: "drum set", snare: "snare drum", tenors: "tenors", bass: "bass drums", cymbals: "cymbals", piano: "piano" };
  return `"${title}": ${parts.map((part) => names[part] || instrumentName(part)).join(" + ")}, ${score.measures.length} ${score.measures.length === 1 ? "bar" : "bars"} of ${timeSig.num}/${timeSig.type}, ${Math.round(tempo)} BPM`;
}
export { TICKS };
